import { getBrowserTrackingConsent } from "./identity"

/**
 * Browser-side calls into the Clarity SDK.
 *
 * Safe to call before Clarity finishes booting: the inline bootstrap installs a
 * queueing stub (`c[a].q.push(arguments)`) as its very first act, so commands
 * issued early are replayed once the real SDK loads. Every helper here no-ops
 * when Clarity is disabled, so nothing needs to know whether it is on.
 */

type ClarityFn = ((...args: unknown[]) => void) & { v?: string; t?: boolean }

function clarity(): ClarityFn | null {
  if (typeof window === "undefined") return null
  const fn = (window as unknown as { clarity?: ClarityFn }).clarity
  return typeof fn === "function" ? fn : null
}

/**
 * Cloudflare's placeholders for "we could not resolve this". Passing them
 * through would create a phantom country in the dashboard that looks real.
 */
const UNRESOLVED_COUNTRIES = new Set(["XX", "T1"])

export function isUsableCountry(country: unknown): country is string {
  return (
    typeof country === "string" &&
    /^[A-Z]{2}$/.test(country) &&
    !UNRESOLVED_COUNTRIES.has(country)
  )
}

/**
 * Record the shopper's TRUE country as a Clarity custom tag.
 *
 * ⚠️ WHY THIS EXISTS. Clarity's built-in Country dimension comes from the IP
 * that connects to its collector. Ad-blocked shoppers reach it through our
 * /api/ms/u fallback, so those sessions are stamped with the SERVER's country
 * (Germany) no matter what — X-Forwarded-For is ignored, and the built-in
 * dimension cannot be corrected. This tag is the only per-session country that
 * is right for BOTH direct and proxied uploads, so segment on `geo_country`
 * rather than on Country whenever ad-blocked traffic matters.
 *
 * Sourced from Cloudflare's CF-IPCountry via /api/identity (already fetched on
 * every page, same-origin, uncached).
 */
export function applyClarityCountry(country: unknown): void {
  const fn = clarity()
  if (!fn || !isUsableCountry(country)) return
  try {
    fn("set", "geo_country", country)
  } catch {
    // Diagnostics must never break a page.
  }
}

/**
 * Mirror the site's consent state into Clarity.
 *
 * The project is configured with `"track":false` (Clarity's cookie-consent
 * mode), so without this it records but never sets _clck/_clsk — meaning no
 * returning-visitor data and no stitching of a shopper across sessions. Rather
 * than hardcode "granted", this forwards the SAME signal every other
 * destination already uses, so one consent change moves everything together.
 */
export function applyClarityConsent(): void {
  const fn = clarity()
  if (!fn) return
  try {
    const consent = getBrowserTrackingConsent()
    fn("consentv2", {
      ad_Storage: consent.ad_storage === "granted" ? "granted" : "denied",
      analytics_Storage:
        consent.analytics_storage === "granted" ? "granted" : "denied",
    })
  } catch {
    // Diagnostics must never break a page.
  }
}
